import { useSyncExternalStore } from 'react'
import type { TkInit, Task, Question } from '../types'
import { ToastFn, UpdateQuestionFn } from '../common/common'
import { QuestionProcessingState } from '../types/QuestionProcessingState'
import { structureToPlainText, plainTextToStructure } from '../comps/TaResponse'
import { AnswerWebResult } from '../types/answerwebresult'

export interface QuestionProgressEvent {
  type: 'questionprogress'
  tasksha?: string
  projectsha?: string
  questionsha: string
  step:
  // | 'started'
  | 'progress_sources'
  | 'progress_structure'
  | 'running_answer'
  | 'chunk_answer'
  | 'reset_answer'
  | 'done_answer'

  | 'running_trace'
  | 'done_trace'
  | 'running_score'
  | 'done_score'
  //general update progress
  | 'status'
  //ended
  | 'end'
  text?: string
  payload?: any
  // UTC ms-since-epoch stamped by the server (SseEvent base class). The
  // frontend uses this to drop stale frames - anything older than the
  // current QuestionProcessingState.updatetime is discarded. Optional on
  // the wire so older / local-only events (no server roundtrip) keep
  // working; missing means "apply unconditionally".
  updatetime?: number
}


export const EMPTY_TKINIT: TkInit = {
  tasks: [], countrunning: 0, countwaiting: 0,
  anyrunning: false, anywaiting: false, anynotrunning: false,
}

// Shared callback shapes - also used by the batch executors below so the
// pass-through signatures stay consistent throughout the service.

// In-flight tkinit requests, keyed by message id. Each entry carries the
// callbacks needed when the matching response arrives (dispatch toasts +
// updateQuestion patches for completed tasks). Resolves the awaiting
// caller's promise with the TkInit result (or `null` on error / timeout).
interface PendingTkInit {
  resolve: (result: TkInit | null) => void
  addToast?: ToastFn
  updateQuestion?: UpdateQuestionFn
  timer: ReturnType<typeof setTimeout>
}
const pendingTkInit: Map<string, PendingTkInit> = new Map()
const TKINIT_TIMEOUT_MS = 10_000

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// Request the current task state. Sends `{ type:'tkinit', id, waitlist }`
// over the taskstream WebSocket and awaits the matching response frame.
// The response is dispatched via `handleTkInitResponse` below - that's where
// the per-task onComplete / dispatchCompletedTasks / reconcile logic
// fires, mirroring the old POST `/api2/tkinit` behaviour. Returns null
// when the socket is closed or the request times out.
export async function fetchTasks(
  waitlist: string[] = [],
  addToast?: ToastFn,
  updateQuestion?: UpdateQuestionFn,
): Promise<TkInit | null> {
  const id = newRequestId()
  return new Promise<TkInit | null>(resolve => {
    const timer = setTimeout(() => {
      if (pendingTkInit.delete(id)) {
        // eslint-disable-next-line no-console
        console.warn('[tkinit] no response in', TKINIT_TIMEOUT_MS, 'ms - giving up on', id)
        resolve(null)
      }
    }, TKINIT_TIMEOUT_MS)

    pendingTkInit.set(id, { resolve, addToast, updateQuestion, timer })

    const sent = sendTaskstreamMessage({ type: 'tkinit', id, waitlist })
    if (!sent) {
      // No live socket. Drop the pending entry now so we don't leak the
      // timer; consumers can retry after the WS reconnects.
      clearTimeout(timer)
      pendingTkInit.delete(id)
      // eslint-disable-next-line no-console
      console.warn('[tkinit] no live socket; skipping request')
      resolve(null)
    }
  })
}

// Apply an inbound `tkinit` response frame to local state. Pulled out of
// fetchTasks so AppContext's WebSocket onmessage handler can forward
// every tkinit frame here regardless of who initiated the request - the
// per-id pendingTkInit map correlates the awaiting promise (if any) and
// the rest of the dispatch runs unconditionally.
export function handleTkInitResponse(event: TkInit & { id?: string }): void {
  const id = event.id
  const pendingReq = id ? pendingTkInit.get(id) : undefined
  if (pendingReq) {
    clearTimeout(pendingReq.timer)
    pendingTkInit.delete(id!)
  }

  // Use the original requester's callbacks when present (so completion
  // toasts fire with their per-call addToast / updateQuestion), otherwise
  // dispatch with no toast - the reconcile + onComplete steps still run.
  const addToast = pendingReq?.addToast
  const updateQuestion = pendingReq?.updateQuestion

  taskCountListener?.(event.countrunning ?? 0, event.countwaiting ?? 0)
  reconcileStreamingFromTkInit(event)
  if (addToast && updateQuestion) dispatchCompletedTasks(event, addToast, updateQuestion)

  // Fire onComplete for any pending tasks (registered via addPendingTask)
  // that just landed in waitlisttasks.
  const doneShas = new Set((event.waitlisttasks ?? []).map((t: Task) => t.tasksha))
  if (doneShas.size > 0) {
    const fired = pending.filter(p => doneShas.has(p.sha))
    pending = pending.filter(p => !doneShas.has(p.sha))
    fired.forEach(p => { try { p.onComplete() } catch { /* ignore */ } })
  }
  tkInitListeners.forEach(fn => fn(event))

  pendingReq?.resolve(event)
}

const seenCompletedTaskShas: Set<string> = new Set()

function dispatchCompletedTasks(
  result: TkInit,
  addToast: ToastFn,
  updateQuestion: UpdateQuestionFn,
): void {
  for (const t of result.waitlisttasks ?? []) {
    dispatchCompletedTask(t, addToast, updateQuestion)
  }
}

// Apply one finished task's resultjson back onto the matching question.
// Deduped via seenCompletedTaskShas so foreground callers (runTrace)
// that dispatch synchronously plus the poll-driven path don't double
// up. Exported so foreground endpoints can bypass the tkinit roundtrip
// entirely when they already have the task in hand.
export function dispatchCompletedTask(
  t: Task,
  addToast: ToastFn,
  updateQuestion: UpdateQuestionFn,
): void {
  if (!t.tasksha || seenCompletedTaskShas.has(t.tasksha)) return
  seenCompletedTaskShas.add(t.tasksha)
  if (!t.resultjson || !t.tasktype) return
  try {
    const parsed = JSON.parse(t.resultjson)
    console.log('tkinit completed: ', t.tasktype);
    if ((t.tasktype === 'workflow' || t.tasktype === 'question') && parsed?.questionsha) {
      //we clear out the trace & scoring if generating a new answer
      updateQuestion(parsed.questionsha, {
        //answer & trace parts
        answerobj: parsed.answerobj,
        structure: parsed.structure,
        sourcedocs: parsed.sourcedocs,
        state: 'draft',
        //scoring part
        bideval: parsed.bideval,
        modified: true,
      })
      addToast('success', 'Question complete')
    }
    else if (t.tasktype === 'tracing' && parsed?.questionsha) {
      updateQuestion(parsed.questionsha, {
        //answer + trace 
        answerobj: parsed.answerobj,
        modified: true,
      })
      addToast('success', 'Tracing complete')
    }
    else if (t.tasktype === 'scoring' && parsed?.questionsha) {
      updateQuestion(parsed.questionsha, {
        //scoring part
        bideval: parsed.bideval,
        modified: true,
      })
      addToast('success', 'Scoring complete')
    }
    else if (t.tasktype === 'improve' && parsed?.questionsha) {
      // Server tasktype for the improvement-suggestion pass. The
      // returned bideval has improvementcategories baked onto each
      // group, so we apply it verbatim and the per-group improvement
      // chips light up immediately.
      updateQuestion(parsed.questionsha, {
        bideval: parsed.bideval,
        modified: true,
      })
      addToast('success', 'Improvements ready')
    }
    else if (t.tasktype === 'upload') {
      addToast('success', 'Upload complete')
    } else if (t.tasktype === 'delete') {
      addToast('success', 'Document deleted')
    } else {
      console.log('tkinit noresult', t.tasktype);
    }
  } catch {
    // Malformed resultjson - skip. The streaming pipeline still covers
    // the happy path; this branch only fires when SSE missed the
    // result anyway, so a parse failure here just means we stay where
    // we were.
    console.log('tkinit malformedresultjson', t.tasktype);
  }
}



// Drive the processingState map from tkinit so the UI catches up even
// when SSE events get dropped: tasks marked finished → mark done; tasks
// marked running → ensure running=true; tasks marked waiting → ensure
// queued=true. Matched by tasksha which startGenerateQuestions records
// on the entry at dispatch time.
function reconcileStreamingFromTkInit(result: TkInit): void {
  const allTasks: Task[] = [...(result.tasks ?? []), ...(result.waitlisttasks ?? [])]
  if (allTasks.length === 0) return
  const byTaskSha = new Map<string, Task>()
  allTasks.forEach(t => { if (t.tasksha) byTaskSha.set(t.tasksha, t) })
  processingState.forEach((entry, questionsha) => {
    if (!entry.tasksha) return
    const t = byTaskSha.get(entry.tasksha)
    if (!t) return
    var handled = false;
    //if its one of the worflow types
    if (t.tasktype == 'workflow'
      || t.tasktype == 'question'
      || t.tasktype === 'tracing'
      || t.tasktype === 'scoring') {
      //queued - we dont check if its already been set so far might need to optimse for that
      // if (t.iswaiting) {
      //   patchQuestionProcessing(questionsha, {
      //     queuedAnswer: t.meta.generateanswer,
      //     queuedTrace: t.meta.generatetrace,
      //     queuedScoring: t.meta.generatescoring,
      //     runningAnswer: false,
      //     runningTrace: false,
      //     runningScoring: false
      //   })
      //   handled = true

      // }//running
      // else 
      if (t.isrunning) {
        patchQuestionProcessing(questionsha, {
          queuedAnswer: false,
          queuedTrace: false,
          queuedScoring: false,
          runningAnswer: t.meta.generateanswer,
          runningTrace: t.meta.generatetrace,
          runningScoring: t.meta.generatescoring,
        })
        handled = true;
      } //finished
      else if (t.isfinished) {
        patchQuestionProcessing(questionsha, {
          queuedAnswer: false,
          queuedTrace: false,
          queuedScoring: false,
          runningAnswer: false,
          runningTrace: false,
          runningScoring: false,
          //and clear the individual tags
          sourcedocs: false,
          structure: false,
          answer: false
        })
        handled = true;

      }
    }
    if (!handled)
      console.log('reconcileStreamingFromTkInit', t.tasktype);
  })
}

// ─── Pending task callbacks ───────────────────────────────────────────────────
// Call addPendingTask(sha, addToast, updateQuestion, callback) after any
// operation that returns a tasksha. The service polls tkinit with the
// accumulated SHAs, applies each completed task's `resultjson` (via the
// fetchTasks → dispatchCompletedTasks pass-through), and fires each
// caller's per-sha onComplete once when its task appears in
// waitlisttasks. `addToast` / `updateQuestion` travel WITH the
// registration so poll() has them at fetch time - no global callback
// registration.

interface Pending {
  sha: string
  onComplete: () => void
  addToast: ToastFn
  updateQuestion: UpdateQuestionFn
}

let pending: Pending[] = []
let timer: ReturnType<typeof setInterval> | null = null
// Fires after every tkinit response or taskqueue push with the latest
// running + waiting counts. AppContext registers a setter that mirrors
// these onto state so the sidebar can show separate "N running" /
// "N queued" badges.
let taskCountListener: ((running: number, waiting: number) => void) | null = null
// tkInit now carries finished tasks only - see the new TaskQueueSnapshot
// + setTaskQueueListener path below for the live queue.
const tkInitListeners = new Set<(result: TkInit) => void>()

// Live queue snapshot pushed by the server on every task state change
// (enqueue / start running / finish / cancel). The TaskManagerTab's
// "queue" sub-tab binds to this; the "finished" sub-tab still pulls via
// tkinit.
export interface TaskQueueSnapshot {
  tasks: Task[]
  countrunning: number
  countwaiting: number
}
const taskQueueListeners = new Set<(snapshot: TaskQueueSnapshot) => void>()
// Remembered last snapshot so a late subscriber (e.g. TaskManagerTab
// mounting after the connect-time push) still gets the current queue.
let lastTaskQueue: TaskQueueSnapshot = { tasks: [], countrunning: 0, countwaiting: 0 }

// Taskstream wake-up. AppContext registers its `kickTaskstream` callback
// here at mount; services that fire streaming jobs (startGenerateQuestions
// etc.) call `kickTaskstream()` exported below to force an immediate SSE
// reconnect when the loop is in its 5-second backoff. No-op if AppContext
// hasn't registered yet (e.g. SSR / tests).
let taskstreamKicker: (() => void) | null = null

export function setTaskstreamKicker(fn: () => void): void {
  taskstreamKicker = fn
}

export function kickTaskstream(): void {
  taskstreamKicker?.()
}

// Taskstream client→server sender. AppContext registers a function that writes
// a JSON message to the live WebSocket; returns false when there's no open
// socket (e.g. on the SSE fallback) so callers can fall back to a REST call.
let taskstreamSender: ((msg: unknown) => boolean) | null = null

export function setTaskstreamSender(fn: (msg: unknown) => boolean): void {
  taskstreamSender = fn
}

export function sendTaskstreamMessage(msg: unknown): boolean {
  return taskstreamSender?.(msg) ?? false
}

export function setTaskCountListener(fn: (running: number, waiting: number) => void): void {
  taskCountListener = fn
}

export function setTkInitListener(fn: (result: TkInit) => void): () => void {
  tkInitListeners.add(fn)
  return () => { tkInitListeners.delete(fn) }
}

// Subscribe to live queue snapshots. The callback fires immediately
// with the last-seen snapshot (or the empty default if nothing has
// arrived yet) so the caller doesn't have to handle the "no data
// yet" race itself, then again on every `taskqueue` push from the
// server.
export function setTaskQueueListener(fn: (snapshot: TaskQueueSnapshot) => void): () => void {
  taskQueueListeners.add(fn)
  try { fn(lastTaskQueue) } catch { /* never let one bad listener break the rest */ }
  return () => { taskQueueListeners.delete(fn) }
}

// Task-level progress listeners. Fire for `questionprogress` events
// that carry a tasksha but no questionsha - currently the document
// extraction flow. The BidImportQuestionsModal subscribes here while
// the extraction is in flight to display the per-page percentage text.
const taskProgressListeners = new Set<(ev: QuestionProgressEvent) => void>()

export function addTaskProgressListener(fn: (ev: QuestionProgressEvent) => void): () => void {
  taskProgressListeners.add(fn)
  return () => { taskProgressListeners.delete(fn) }
}

// Apply an inbound `taskqueue` push frame. Called from AppContext's
// WebSocket onmessage handler. Also updates the running/queued count
// listener so the sidebar chips stay in sync without waiting for the
// next tkinit poll.
export function handleTaskQueuePush(event: { tasks?: Task[]; countrunning?: number; countwaiting?: number }): void {
  const snapshot: TaskQueueSnapshot = {
    tasks: event.tasks ?? [],
    countrunning: event.countrunning ?? 0,
    countwaiting: event.countwaiting ?? 0,
  }
  lastTaskQueue = snapshot
  taskCountListener?.(snapshot.countrunning, snapshot.countwaiting)
  taskQueueListeners.forEach(fn => { try { fn(snapshot) } catch { /* ignore */ } })
}

export function addPendingTask(
  sha: string,
  addToast: ToastFn,
  updateQuestion: UpdateQuestionFn,
  onComplete: () => void = () => { },
): void {
  console.log('addtask', sha);
  pending.push({ sha, onComplete, addToast, updateQuestion })
  if (!timer) timer = setInterval(poll, 3000)
}

async function poll() {
  if (pending.length === 0) {
    clearInterval(timer!)
    timer = null
    return
  }
  try {
    // All pending tasks share the React-side callbacks (useCallback []
    // identity is stable), so picking the first entry's pair is
    // sufficient. fetchTasks does the completion dispatch AND fires
    // onComplete for any pending shas that landed in waitlisttasks, so
    // poll's only remaining job is the periodic kick + timer teardown.
    const { addToast, updateQuestion } = pending[0]
    await fetchTasks(pending.map(p => p.sha), addToast, updateQuestion)
  } catch { /* retry next interval */ }
  if (pending.length === 0) {
    clearInterval(timer!)
    timer = null
  }
}


// Shared idle/empty instance - callers can read fields off this without
// null-checks. Treat it as immutable; every write path constructs its
// own instance via Object.assign(new QuestionProcessingState(), …).
export const EMPTY_PROCESSING: QuestionProcessingState = Object.assign(
  new QuestionProcessingState(),
  { queued: false, running: false },
)

const processingState: Map<string, QuestionProcessingState> = new Map()

let processingVersion = 0
const processingListeners: Set<() => void> = new Set()

function notifyProcessing() {
  processingVersion = (processingVersion + 1) | 0
  processingListeners.forEach(cb => { try { cb() } catch { /* ignore */ } })
}

function subscribeProcessing(cb: () => void): () => void {
  processingListeners.add(cb)
  return () => { processingListeners.delete(cb) }
}

// Snapshot read for a question. Returns the empty/idle state when nothing
// is registered yet, so callers never have to null-check.
export function getQuestionProcessing(sha: string): QuestionProcessingState {
  return processingState.get(sha) ?? EMPTY_PROCESSING
}

// Set a single field. No-ops when the value already matches so the listener
// fanout stays cheap for repeated flips. Uses Object.assign onto a fresh
// instance so the QuestionProcessingState prototype (and its getters)
// survives the patch.
export function setQuestionProcessing(
  sha: string,
  field: keyof QuestionProcessingState,
  value: QuestionProcessingState[keyof QuestionProcessingState],
): void {
  const current = processingState.get(sha) ?? EMPTY_PROCESSING
  if (current[field] === value) return
  const next = Object.assign(new QuestionProcessingState(), current, { [field]: value })
  processingState.set(sha, next)
  notifyProcessing()
}

// Merge a partial patch into a question's processing entry. Lets a single
// event flip several fields at once (status + queued/running + log
// + tasksha) without firing a notify per write. Same Object.assign onto
// a fresh instance pattern as setQuestionProcessing so the class
// prototype is preserved.
//
// Special-case: `log` entries in the patch are APPENDED to the existing
// log rather than replacing it, so successive writes accumulate the
// timeline. Callers that want to wipe the log should go through
// clearQuestionProcessing instead.
export function patchQuestionProcessing(
  sha: string,
  patch: Partial<QuestionProcessingState>,
): void {
  const current = processingState.get(sha) ?? EMPTY_PROCESSING
  const patchTime = patch.updatetime ?? Date.now()
  if (patchTime < current.updatetime) {
    return
  }
  const next = Object.assign(new QuestionProcessingState(), current, patch, { updatetime: patchTime })
  if (patch.log && patch.log.length > 0) {
    const prevLog = current.log ?? []
    next.log = [...prevLog, ...patch.log]
  }
  processingState.set(sha, next)
  notifyProcessing()
}

// Wipe a question's entry - used when a workflow is cleared so the
// spinner state doesn't carry over.
export function clearQuestionProcessing(sha: string): void {
  if (!processingState.has(sha)) return
  processingState.delete(sha)
  notifyProcessing()
}

// Wipe only the activity log for a question (queued/running/etc flags
// stay). patchQuestionProcessing appends to log rather than replacing,
// so callers that want to empty the timeline go through here instead.
export function clearQuestionProcessingLog(sha: string): void {
  const current = processingState.get(sha)
  if (!current || !current.log || current.log.length === 0) return
  const next = Object.assign(new QuestionProcessingState(), current, { log: [] })
  processingState.set(sha, next)
  notifyProcessing()
}



// Hook: subscribe a component to the registry's view of one question.
// Re-renders only when its entry changes (or, conservatively, when any
// entry changes - the snapshot return is the per-sha slice).
export function useQuestionProcessing(sha: string): QuestionProcessingState {
  return useSyncExternalStore(
    subscribeProcessing,
    () => processingState.get(sha) ?? EMPTY_PROCESSING,
    () => EMPTY_PROCESSING,
  )
}

// Hook: bump counter for list contexts that want to read many shas in one
// pass without subscribing one-by-one.
export function useQuestionProcessingVersion(): number {
  return useSyncExternalStore(subscribeProcessing, () => processingVersion, () => 0)
}


export function getQuestionProcessingSnapshot(): Record<string, QuestionProcessingState> {
  const out: Record<string, QuestionProcessingState> = {}
  processingState.forEach((v, k) => { out[k] = v })
  return out
}



export function dispatchQuestionProgress(ev: QuestionProgressEvent,
  getQuestion: (sha: string) => Question | null,
  updateQuestion: UpdateQuestionFn): void {
  if (!ev.questionsha) {
    taskProgressListeners.forEach(fn => { try { fn(ev) } catch { /* ignore */ } })
    return
  }
  applyProgressEvents(ev)
  const q = getQuestion(ev.questionsha)
  if (q) {
    const patch = patchQuestionWithProgressEvent(q, ev)
    if (Object.keys(patch).length > 0) {
      console.log('updating Q', Object.keys(patch));
      updateQuestion(ev.questionsha, patch);
    }

  } else {
    console.log('noquestion', ev);
  }
}

// Apply an SSE event to the processingState map. Updates `status`,
// `log`, `tasksha`, the answer pipeline's `queuedAnswer`/`runningAnswer`
// pair, and the per-substep boolean flags (sourcedocs/structure/answer).
// Has side effects; call ONCE per event.
export function applyProgressEvents(ev: QuestionProgressEvent): void {
  if (ev.step !== 'chunk_answer') {
    console.log('[questionprogress]', ev.step, ev.questionsha, ev.text ?? '', ev.payload ?? '')
  }
  const cur = processingState.get(ev.questionsha) ?? EMPTY_PROCESSING
  // Drop frames that arrive out of order. SseEvent stamps updatetime in
  // UTC ms-since-epoch so this comparison is timezone-safe regardless of
  // where the API server runs. Events without a timestamp are applied
  // unconditionally (older local-only paths).
  if (ev.updatetime != null && ev.updatetime < cur.updatetime) {
    return
  }
  const next: QuestionProcessingState = Object.assign(
    new QuestionProcessingState(),
    cur,
    ev.tasksha && cur.tasksha !== ev.tasksha ? { tasksha: ev.tasksha } : null,
    ev.updatetime != null ? { updatetime: ev.updatetime } : null,
  )

  switch (ev.step) {

    case 'progress_sources':
      next.sourcedocs = false
      break
    case 'progress_structure':
      next.structure = false
      break
    case 'running_answer':
      // next.sourcedocs = true
      // next.structure = true
      // next.answer = true
      next.queuedAnswer = false
      next.runningAnswer = true
      break
    case 'done_answer':
      next.answer = false
      next.queuedAnswer = false
      next.runningAnswer = false
      break
    case 'running_trace': {
      next.queuedTrace = false
      next.runningTrace = true
      break
    }
    case 'done_trace': {
      next.queuedTrace = false
      next.runningTrace = false
      break
    }
    case 'running_score': {
      next.queuedScoring = false
      next.runningScoring = true
      break
    }
    case 'done_score': {
      next.queuedScoring = false
      next.runningScoring = false
      break
    }
    case 'status':
    case 'end':
    default:
      //do nothing out the ordinary
      break;
  }

  let logText = ev.text ?? '';
  if (logText) {
    const prevLog = next.log ?? []
    next.log = [...prevLog, { ts: Date.now(), step: ev.step, text: logText }]
  }
  processingState.set(ev.questionsha, next)
  notifyProcessing()
}

// Pure: returns the data-field delta for one event as a Partial<Question>.
// Reads `question` only where the new value depends on the current one
// (e.g. answerchunk appends to the existing answer text). Empty object
// for events that carry no data-field changes - the caller treats that
// as a no-op. Returned shape is suitable for passing straight to
// AppContext.updateQuestion, which merges + flips `modified: true`.
function patchQuestionWithProgressEvent(question: Question, ev: QuestionProgressEvent): Partial<Question> {
  switch (ev.step) {
    case 'progress_sources': {
      // const sources = Array.isArray(ev.payload) ? ev.payload : ev.payload?.sourcedocs ?? []
      return { sourcedocs: ev.payload?.sourcedocs ?? [] }
    }
    case 'progress_structure': {
      // const structure = typeof ev.payload === 'string' ? ev.payload : ev.payload?.structure ?? ''
      return { structure: ev.payload?.structure ?? '' }
    }
    case 'chunk_answer': {
      // Incrementally rebuild the answerstructure from the accumulated
      // streamed text. We don't have sentence boundaries during
      // streaming, so each line becomes a single sentence - paragraphs
      // / lines are recovered by splitting on `\n\n` / `\n` to mirror
      // the structureToPlainText shape.
      const curText = structureToPlainText(question.answerobj?.answerstructure)
      const nextText = curText + (ev.text ?? '')
      return {
        answerobj: {
          ...(question.answerobj ?? {} as AnswerWebResult),
          answerstructure: plainTextToStructure(nextText),
        },
      }
    }
    case 'reset_answer':
      return {
        answerobj: {
          ...(question.answerobj ?? {} as AnswerWebResult),
          answerstructure: plainTextToStructure(''),
        },
      }
    case 'done_answer':
      return { answerobj: ev.payload.answerobj }
    case 'done_trace':
      return { answerobj: ev.payload.answerobj }
    case 'done_score':
      return { bideval: ev.payload.bideval }
    // case 'end': {
    //   return {}
    // }
    default:
      return {}
  }
}